
import Button from '@mui/material/Button'
import { ComposerMediaContext } from 'components/ComposerMediaContext'
import { RecycleSelector } from 'components/Posts/components/RecycleSelector'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { getQueuePostAction, resetComposer, setRecycle, setSchedule } from 'services/compose'
import {
  activePostIdSelector,
  composerAltTextErrorSelector,
  composerBulkFileSelector,
  composerImagesSelector,
  composerIsEmptySelector,
  composerReadinessSelector,
  composerRecycleSelector,
  composerResetKeySelector,
  composerScheduleSelector,
  composerSelectedBucketIdSelector,
  composerSelectedProfilesCountSelector,
  composerTabsSelector,
  composerVideoSelector,
  multiPostsCountSelector
} from 'services/compose/selectors'
import { getDefaultScheduleTime } from 'services/compose/state'
import { mergeFilesState } from 'services/compose/utils'
import { hasNetworkFileErrors } from 'services/compose/validation/fileValidators'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import Accordion from '../Accordion'
import SaveDraftButton from '../Header/SaveDraftButton'
import LoadingOverlay from '../LoadingOverlay'
import ComposerScheduleForm from '../ScheduleOptions/ComposerScheduleForm'
import styles from './Actions.pcss'
import AddToBucketButton from './AddToBucketButton'
import BulkActions from './BulkActions'
import ComposerActionsButton from './ComposerActionsButton'
import { useCreatePost } from './hooks/useCreatePost'
import { useSubmitShortcut } from './hooks/useSubmitShortcut'
import { useUpdatePost } from './hooks/useUpdatePost'
import { QueuePostsActionSelect } from './QueuePostsActionSelect'
import { StatusBar } from './StatusBar'

interface ActionsProps {
  onPostCreateStarted: (redirectUrl?: string) => void
  activeView: 'default' | 'bulk'
}

export function Actions(props: ActionsProps) {
  const { onPostCreateStarted } = props
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { files: contextFiles } = useContext(ComposerMediaContext)
  const images = useSelector(composerImagesSelector)
  const video = useSelector(composerVideoSelector)
  const selectedProfilesCount = useSelector(composerSelectedProfilesCountSelector)
  const multiPostsCount = useSelector(multiPostsCountSelector)
  const bulkUploadFile = useSelector(composerBulkFileSelector)
  const readiness = useSelector(composerReadinessSelector)
  const altTextError = useSelector(composerAltTextErrorSelector)
  const hasProfiles = selectedProfilesCount > 0
  const isInEditMode = Boolean(useSelector(activePostIdSelector))
  const isEmpty = useSelector(composerIsEmptySelector)
  const networks = useSelector(composerTabsSelector)
  const schedule = useSelector(composerScheduleSelector)
  const recycle = useSelector(composerRecycleSelector)
  const selectedBucketId = useSelector(composerSelectedBucketIdSelector)
  const resetKey = useSelector(composerResetKeySelector)
  const [isOpen, setIsOpen] = useState(!isEmpty)
  const queuePostAction: string = useSelector(getQueuePostAction)

  const files = useMemo(() => {
    return mergeFilesState(images, video, contextFiles)
  }, [contextFiles, images, video])

  const fileValidationError = useMemo(
    () => hasNetworkFileErrors(networks, Object.values(files)),
    [files, networks]
  )

  const isReady = !readiness.isUploading && !readiness.hasErrors && hasProfiles && !fileValidationError && !altTextError

  const { submitPost, fetchingPosts, isCreatingNew } = useCreatePost(files, onPostCreateStarted)
  const { saveEdit, isUpdating } = useUpdatePost(files)
  const busy = fetchingPosts || isUpdating

  useEffect(() => {
    setIsOpen(isInEditMode || !isEmpty || props.activeView === 'bulk')
  }, [isEmpty, props.activeView, isInEditMode])

  const onSectionExpandedToggle = (nextValue: boolean) => {
    setIsOpen(nextValue)
  }

  let postsCount = selectedProfilesCount
  if (props.activeView === 'bulk') {
    postsCount = bulkUploadFile?.rows || multiPostsCount
  }

  useEffect(() => {
    if (schedule.isActive) {
      const timeout = 400
      const timerId = setTimeout(() => {
        const mainSection = document.querySelector('[data-test="composer"]')
        if (mainSection) {
          const top = mainSection.scrollHeight
          mainSection.scrollTo({
            top,
            left: 0,
            behavior: 'smooth'
          })
        }
      }, timeout)

      return () => { clearTimeout(timerId) }
    }
    return undefined
  }, [schedule.isActive])

  const saveToBucket = useCallback(() => {
    if (selectedBucketId) {
      if (isInEditMode) {
        saveEdit()
      } else {
        submitPost({ bucketId: selectedBucketId, shareType: queuePostAction === 'top' ? 'next' : 'queue' })
      }
    }
  }, [isInEditMode, selectedBucketId, queuePostAction, saveEdit, submitPost])

  const schedulePost = useCallback(() => {
    submitPost({ scheduled: true })
  }, [submitPost])

  const addToPlan = useCallback(() => {
    submitPost({ shareType: queuePostAction === 'top' ? 'next' : 'queue' })
  }, [submitPost, queuePostAction])

  const shareNow = useCallback(() => {
    submitPost({ shareType: 'now' })
  }, [submitPost])

  const addToPlanCreateNew = useCallback(() => {
    submitPost({ shareType: queuePostAction === 'top' ? 'next' : 'queue', createNew: true })
  }, [submitPost, queuePostAction])

  const shareNowCreateNew = useCallback(() => {
    submitPost({ shareType: 'now', createNew: true })
  }, [submitPost])

  const schedulePostCreateNew = useCallback(() => {
    submitPost({ scheduled: true, createNew: true })
  }, [submitPost])

  const saveToBucketCreateNew = useCallback(() => {
    if (selectedBucketId) {
      submitPost({
        bucketId: selectedBucketId,
        shareType: queuePostAction === 'top' ? 'next' : 'queue',
        createNew: true
      })
    }
  }, [selectedBucketId, queuePostAction, submitPost])

  const submitByShortcut = useCallback(() => {
    if (schedule.isActive) {
      schedulePost()
      return
    }
    if (selectedBucketId) {
      saveToBucket()
      return
    }
    if (isInEditMode) {
      saveEdit()
    } else {
      shareNow()
    }
  }, [schedule.isActive, selectedBucketId, isInEditMode, schedulePost, saveToBucket, saveEdit, shareNow])

  useSubmitShortcut({
    enabled: props.activeView === 'default',
    isReady,
    onSubmit: submitByShortcut
  })

  const onRecycleChange = (value: boolean | Date | number) => { dispatch(setRecycle(value)) }

  const toggleScheduleActive = () => {
    if (!schedule.isActive) {
      const dateTime = getDefaultScheduleTime().toISOString()
      dispatch(setSchedule({ time: dateTime, date: dateTime }))
    }
    dispatch(setSchedule({ isActive: !schedule.isActive }))
  }

  const onScheduleError = (e: Error) => {
    dispatch(message('An error occurred: ' + e.message, 'error'))
  }

  const onMultiPostCreated = (redirectUrl: string) => {
    navigate(redirectUrl, {
      state: {
        refresh: Date.now().toString(),
        search: location.state?.search
      }
    })
    dispatch(resetComposer())
  }

  if (props.activeView === 'bulk' && postsCount === 0) {
    return null
  }

  const showDefaultActions = !isInEditMode && !selectedBucketId

  return (
    <Accordion
      key={resetKey}
      title={<FormattedMessage id={isInEditMode ? 'composer.titles.confirm-edit' : 'composer.titles.create'} />}
      order={isInEditMode ? '2' : '4'}
      open={isOpen}
      id={isInEditMode ? 'composer-actions-edit' : 'composer-actions'}
      onToggle={onSectionExpandedToggle}
    >
      {props.activeView === 'default' ? (
        <div data-testid="composer-actions">
          <div className={styles.top}>
            <div className={styles.left}>
              <StatusBar
                isReady={isReady}
                isOptimized={readiness.isOptimized}
                isEmpty={isEmpty}
                isInEditMode={isInEditMode}
                emptyNetworks={readiness.emptyNetworks}
                networksCount={networks.length}
              />
            </div>
            <div className={styles.right}>
              {!schedule.isActive && (
                <RecycleSelector
                  value={recycle}
                  horizontal
                  withIcon
                  className={styles['btn-recycle']}
                  onChange={onRecycleChange}
                />
              )}
              {isInEditMode && !selectedBucketId && (
                <Button
                  color="primary"
                  variant="contained"
                  disabled={!isReady || isUpdating}
                  className={styles['btn-edit']}
                  onClick={saveEdit}
                >
                  <FormattedMessage id="composer.actions.save-edit" />
                </Button>
              )}
              {!isInEditMode && <SaveDraftButton className={styles['btn-draft']} />}
              {(showDefaultActions || selectedBucketId) && (
                <QueuePostsActionSelect disabled={!isReady || schedule.isActive} className={styles['btn-queue-action']} />
              )}
              {showDefaultActions && (
                <ComposerActionsButton
                  disabled={!isReady || schedule.isActive || busy}
                  className={styles['btn-group']}
                  btnClassName={styles['btn-actions']}
                  postsCount={selectedProfilesCount}
                  onAddToPlan={addToPlan}
                  onAddToPlanCreateNew={addToPlanCreateNew}
                  onShareNow={shareNow}
                  onShareNowCreateNew={shareNowCreateNew}
                  onSchedule={toggleScheduleActive}
                />
              )}
              {selectedBucketId && (
                <AddToBucketButton
                  disabled={!isReady || busy}
                  postsCount={selectedProfilesCount}
                  className={styles['btn-bucket']}
                  onSubmit={saveToBucket}
                  onSubmitCreateNew={isInEditMode ? undefined : saveToBucketCreateNew}
                />
              )}
            </div>
          </div>
          <div className={`${styles['schedule-box']} ${schedule.isActive ? styles.active : ''}`}>
            <ComposerScheduleForm
              submitDisabled={!isReady || busy}
              hideActions={isInEditMode}
              className={styles['schedule-content']}
              onSubmit={schedulePost}
              onSubmitCreateNew={schedulePostCreateNew}
              onCancel={toggleScheduleActive}
              onError={onScheduleError}
            />
          </div>
          <LoadingOverlay open={isCreatingNew} message={<FormattedMessage id="composer.labels.creating-post" />} />
        </div>
      ) : (
        <BulkActions onMultiPostCreated={onMultiPostCreated} onBulkUploadStarted={props.onPostCreateStarted} />
      )}
    </Accordion>
  )
}

export default Actions
